import {
  IActionMapping,
  TreeComponent,
  TreeNode
} from '@ali-hm/angular-tree-component';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import uFuzzy from '@leeoniya/ufuzzy';
import { NgxSpinnerService } from 'ngx-spinner';
import { interval, of, type Subscription } from 'rxjs';
import { exhaustMap, finalize, map, take, tap } from 'rxjs/operators';
import { ResponseInfoStatusEnum } from '#common/enums/response-info-status.enum';
import { ToBackendRequestInfoNameEnum } from '#common/enums/to/to-backend-request-info-name.enum';
import { isDefined } from '#common/functions/is-defined';
import { isDefinedAndNotEmpty } from '#common/functions/is-defined-and-not-empty';
import { makeCopy } from '#common/functions/make-copy';
import type {
  ColumnCombinedReference,
  CombinedSchemaItem
} from '#common/zod/backend/connection-schemas/combined-schema';
import type { RawSchemaForeignKey } from '#common/zod/backend/connection-schemas/raw-schema';
import type { CachedColumn } from '#common/zod/to-backend/connections/cached-column';
import type { ToBackendClearCachedColumnResponse } from '#common/zod/to-backend/connections/to-backend-clear-cached-column';
import type { ToBackendGetCachedColumnsResponse } from '#common/zod/to-backend/connections/to-backend-get-cached-columns';
import type { ToBackendGetConnectionSampleResponse } from '#common/zod/to-backend/connections/to-backend-get-connection-sample';
import type {
  ToBackendGetConnectionSchemasRequestPayload,
  ToBackendGetConnectionSchemasResponse
} from '#common/zod/to-backend/connections/to-backend-get-connection-schemas';
import type { ToBackendRefreshCachedColumnResponse } from '#common/zod/to-backend/connections/to-backend-refresh-cached-column';
import type { ToBackendViewCachedColumnResponse } from '#common/zod/to-backend/connections/to-backend-view-cached-column';
import { NavQuery, NavState } from '#front/app/queries/nav.query';
import { ApiService } from '#front/app/services/api.service';
import { MyDialogService } from '#front/app/services/my-dialog.service';
import { SampleDialogData } from './sample-dialog/sample-dialog.component';
import { SchemaGraphDialogData } from './schema-graph-dialog/schema-graph-dialog.component';
import { ViewCachedUniqueValuesDialogData } from './view-cached-unique-values-dialog/view-cached-unique-values-dialog.component';

let SCHEMAS_SPINNER_NAME = 'schemasRefresh';

interface SchemaTreeNode {
  id: string;
  name: string;
  searchName: string;
  children?: SchemaTreeNode[];
  nodeType: 'connection' | 'table' | 'column' | 'index' | 'error';
  connectionId?: string;
  schemaDisplayName?: string;
  tableName?: string;
  tableType?: string;
  columnName?: string;
  dataType?: string;
  isNullable?: boolean;
  isPrimaryKey?: boolean;
  isUnique?: boolean;
  cacheUniqueValues?: boolean;
  foreignKeys?: RawSchemaForeignKey[];
  references?: ColumnCombinedReference[];
  description?: string;
  example?: string;
  errorMessage?: string;
  cachedColumn?: CachedColumn | null;
  hasUncachedCacheUniqueValuesRecommendation?: boolean;
}

@Component({
  standalone: false,
  selector: 'm-schemas',
  templateUrl: './schemas.component.html',
  styleUrls: ['schemas.component.scss']
})
export class SchemasComponent implements OnInit, OnDestroy {
  schemasLoaded = false;
  isRefreshing = false;
  spinnerName = SCHEMAS_SPINNER_NAME;
  sampleSpinnerNodeId: string;
  sampleRequestId = 0;
  cachedColumnSpinnerNodeIds = new Set<string>();
  cachedColumnsPollingSub: Subscription;
  treeNodes: SchemaTreeNode[] = [];
  filteredTreeNodes: SchemaTreeNode[] = [];
  combinedSchemaItems: CombinedSchemaItem[] = [];

  searchWord: string;
  searchTimer: any;
  showIndexes = false;
  showQuestionMarks = false;

  prevRepoId: string;
  prevBranchId: string;
  prevEnvId: string;

  nav: NavState;
  nav$ = this.navQuery.select().pipe(
    tap(x => {
      this.nav = x;

      let isNavChanged =
        isDefined(this.prevRepoId) &&
        isDefined(this.prevBranchId) &&
        isDefined(this.prevEnvId) &&
        (this.prevRepoId !== this.nav.repoId ||
          this.prevBranchId !== this.nav.branchId ||
          this.prevEnvId !== this.nav.envId);

      this.prevRepoId = this.nav.repoId;
      this.prevBranchId = this.nav.branchId;
      this.prevEnvId = this.nav.envId;

      this.cd.detectChanges();

      if (isNavChanged && isDefined(this.nav.projectId)) {
        this.loadSchemas({ isRefreshExistingCache: false });
      }
    })
  );

  actionMapping: IActionMapping = {
    mouse: {}
  };

  treeOptions = {
    actionMapping: this.actionMapping,
    displayField: 'name'
  };

  @ViewChild('itemsTree') itemsTree: TreeComponent;

  constructor(
    private navQuery: NavQuery,
    private apiService: ApiService,
    private myDialogService: MyDialogService,
    private cd: ChangeDetectorRef,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit() {
    this.loadSchemas({ isRefreshExistingCache: false });
  }

  ngOnDestroy() {
    this.stopCachedColumnsPolling();

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  }

  loadSchemas(item: { isRefreshExistingCache: boolean }) {
    let { isRefreshExistingCache } = item;

    let nav = this.navQuery.getValue();

    let payload: ToBackendGetConnectionSchemasRequestPayload = {
      projectId: nav.projectId,
      envId: nav.envId,
      repoId: nav.repoId,
      branchId: nav.branchId,
      isRefreshExistingCache: isRefreshExistingCache
    };

    this.isRefreshing = true;
    this.spinner.show(SCHEMAS_SPINNER_NAME);
    this.cd.detectChanges();

    this.apiService
      .req({
        pathInfoName:
          ToBackendRequestInfoNameEnum.ToBackendGetConnectionSchemas,
        payload: payload
      })
      .pipe(
        map((resp: ToBackendGetConnectionSchemasResponse) => {
          if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            this.combinedSchemaItems = resp.payload.combinedSchemaItems;
            this.treeNodes = this.buildTreeNodes({
              combinedSchemaItems: this.combinedSchemaItems
            });
            this.applyFilter();
            this.syncRunningCachedColumnSpinners();
            this.startCachedColumnsPolling();
          }

          this.isRefreshing = false;
          this.spinner.hide(SCHEMAS_SPINNER_NAME);
          this.schemasLoaded = true;
          this.cd.detectChanges();
        }),
        take(1)
      )
      .subscribe();
  }

  nodeOnClick(item: { node: TreeNode }) {
    let { node } = item;
    if (node.hasChildren) {
      node.toggleExpanded();
    }
  }

  searchWordChange() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = setTimeout(() => {
      this.applyFilter();
      this.cd.detectChanges();
    }, 600);
  }

  resetSearch() {
    this.searchWord = undefined;
    this.applyFilter();
    this.cd.detectChanges();
  }

  toggleQuestionMarks() {
    this.showQuestionMarks = !this.showQuestionMarks;
  }

  toggleIndexes() {
    this.showIndexes = !this.showIndexes;
    this.applyFilter();
    this.cd.detectChanges();
  }

  applyFilter() {
    let nodes: SchemaTreeNode[] = makeCopy(this.treeNodes);

    if (!this.showIndexes) {
      nodes = this.removeIndexNodes({ nodes: nodes });
    }

    if (isDefinedAndNotEmpty(this.searchWord)) {
      nodes = nodes.filter(node =>
        this.filterNode({ node: node, term: this.searchWord })
      );
    }

    this.filteredTreeNodes = nodes;
  }

  removeIndexNodes(item: { nodes: SchemaTreeNode[] }): SchemaTreeNode[] {
    let { nodes } = item;
    return nodes.map(node => {
      if (node.children) {
        node.children = this.removeIndexNodes({ nodes: node.children }).filter(
          child => child.nodeType !== 'index'
        );
      }
      return node;
    });
  }

  filterNode(item: { node: SchemaTreeNode; term: string }): boolean {
    let { node, term } = item;

    let selfMatch =
      node.nodeType === 'column' &&
      this.schemaSearchFn({
        term: term,
        searchName: node.searchName
      });

    if (node.children?.length > 0) {
      node.children = node.children.filter(child =>
        this.filterNode({ node: child, term: term })
      );
      return selfMatch || node.children.length > 0;
    }

    return selfMatch;
  }

  schemaSearchFn(item: { term: string; searchName: string }): boolean {
    let { term, searchName } = item;
    let haystack = [searchName];
    let opts = {};
    let uf = new uFuzzy(opts);
    let idxs = uf.filter(haystack, term);
    return idxs != null && idxs.length > 0;
  }

  isCacheRecommendedAndEmpty(item: { node: SchemaTreeNode }): boolean {
    let { node } = item;
    return (
      node.nodeType === 'column' &&
      node.cacheUniqueValues === true &&
      !node.cachedColumn
    );
  }

  syncCacheUniqueValuesRecommendationWarnings(item: {
    nodes: SchemaTreeNode[];
  }) {
    let { nodes } = item;

    nodes.forEach(node => {
      if (node.children?.length > 0) {
        this.syncCacheUniqueValuesRecommendationWarnings({
          nodes: node.children
        });
      }

      node.hasUncachedCacheUniqueValuesRecommendation =
        this.isCacheRecommendedAndEmpty({ node: node }) ||
        node.children?.some(
          child => child.hasUncachedCacheUniqueValuesRecommendation
        ) === true;
    });
  }

  buildTreeNodes(item: {
    combinedSchemaItems: CombinedSchemaItem[];
  }): SchemaTreeNode[] {
    let { combinedSchemaItems } = item;

    let nodes: SchemaTreeNode[] = [];

    combinedSchemaItems.forEach(cs => {
      if (isDefined(cs.errorMessage)) {
        nodes.push({
          id: `${cs.connectionId}__error`,
          name: `${cs.connectionId} - Error: ${cs.errorMessage}`,
          searchName: cs.connectionId,
          nodeType: 'error',
          errorMessage: cs.errorMessage
        });
        return;
      }

      cs.schemas.forEach(combinedSchema => {
        let schemaName = combinedSchema.schemaName;

        let tableChildren: SchemaTreeNode[] = combinedSchema.tables.map(
          table => {
            let columnChildren: SchemaTreeNode[] = table.columns.map(
              (col, colIdx) => ({
                id: `${cs.connectionId}__${schemaName}__${table.tableName}__col__${colIdx}`,
                name: col.columnName,
                searchName: col.columnName,
                nodeType: 'column' as const,
                connectionId: cs.connectionId,
                schemaDisplayName: schemaName,
                tableName: table.tableName,
                columnName: col.columnName,
                dataType: col.dataType,
                isNullable: col.isNullable,
                isPrimaryKey: col.isPrimaryKey,
                isUnique: col.isUnique,
                cacheUniqueValues: col.cacheUniqueValues,
                foreignKeys: col.foreignKeys,
                references: col.references,
                description: col.description,
                example: col.example,
                cachedColumn: col.cachedColumn
              })
            );

            let indexChildren: SchemaTreeNode[] = table.indexes.map(
              (idx, idxIdx) => {
                let label = idx.isPrimaryKey
                  ? `${idx.indexName} (PRIMARY)`
                  : idx.isUnique
                    ? `${idx.indexName} (UNIQUE)`
                    : idx.indexName;

                return {
                  id: `${cs.connectionId}__${schemaName}__${table.tableName}__idx__${idxIdx}`,
                  name: `${label}: ${idx.indexColumns.join(', ')}`,
                  searchName: idx.indexName,
                  nodeType: 'index' as const
                };
              }
            );

            let tableNode: SchemaTreeNode = {
              id: `${cs.connectionId}__${schemaName}__${table.tableName}`,
              name: table.tableName,
              searchName: table.tableName,
              nodeType: 'table' as const,
              connectionId: cs.connectionId,
              schemaDisplayName: schemaName,
              tableName: table.tableName,
              tableType: table.tableType,
              description: table.description,
              children: [...columnChildren, ...indexChildren]
            };

            return tableNode;
          }
        );

        let connectionNode: SchemaTreeNode = {
          id: `${cs.connectionId}__${schemaName}`,
          name: `${cs.connectionId} - ${schemaName}`,
          searchName: `${cs.connectionId} ${schemaName}`,
          nodeType: 'connection',
          connectionId: cs.connectionId,
          schemaDisplayName: schemaName,
          description: combinedSchema.description,
          children: tableChildren
        };

        nodes.push(connectionNode);
      });
    });

    nodes.sort((a, b) => a.name.localeCompare(b.name));
    this.syncCacheUniqueValuesRecommendationWarnings({ nodes: nodes });

    return nodes;
  }

  graphAllOnClick() {
    let nav = this.navQuery.getValue();

    let dialogData: SchemaGraphDialogData = {
      combinedSchemaItems: this.combinedSchemaItems,
      connectionId: undefined,
      schemaName: undefined,
      tableName: undefined,
      projectId: nav.projectId,
      envId: nav.envId
    };

    this.myDialogService.showSchemaGraphDialog(dialogData);
  }

  graphOnClick(item: { node: TreeNode; event: MouseEvent }) {
    let { node, event } = item;
    event.stopPropagation();

    if (isDefined(this.sampleSpinnerNodeId)) {
      return;
    }

    let data = node.data as SchemaTreeNode;
    let nav = this.navQuery.getValue();

    let dialogData: SchemaGraphDialogData = {
      combinedSchemaItems: this.combinedSchemaItems,
      connectionId: data.connectionId,
      schemaName: data.schemaDisplayName,
      tableName: undefined,
      projectId: nav.projectId,
      envId: nav.envId
    };

    this.myDialogService.showSchemaGraphDialog(dialogData);
  }

  copyToClipboard(item: { text: string; event: MouseEvent }) {
    let { text, event } = item;
    event.stopPropagation();
    navigator.clipboard.writeText(text);
  }

  getColumnNodes(item: { nodes: SchemaTreeNode[] }): SchemaTreeNode[] {
    let { nodes } = item;
    let columnNodes: SchemaTreeNode[] = [];

    nodes.forEach(node => {
      if (node.nodeType === 'column') {
        columnNodes.push(node);
      }

      if (node.children?.length > 0) {
        columnNodes.push(...this.getColumnNodes({ nodes: node.children }));
      }
    });

    return columnNodes;
  }

  updateCachedColumn(item: {
    nodeId: string;
    cachedColumn: CachedColumn | null | undefined;
  }) {
    let { nodeId, cachedColumn } = item;
    let columnNodes = this.getColumnNodes({ nodes: this.treeNodes });
    let columnNode = columnNodes.find(x => x.id === nodeId);

    if (isDefined(columnNode)) {
      columnNode.cachedColumn = cachedColumn;
      this.syncCacheUniqueValuesRecommendationWarnings({
        nodes: this.treeNodes
      });
    }
  }

  isCachedColumnSpinning(item: { nodeId: string }): boolean {
    let { nodeId } = item;
    return this.cachedColumnSpinnerNodeIds.has(nodeId);
  }

  addCachedColumnSpinner(item: { nodeId: string }) {
    let { nodeId } = item;
    this.cachedColumnSpinnerNodeIds.add(nodeId);
  }

  removeCachedColumnSpinner(item: { nodeId: string }) {
    let { nodeId } = item;
    this.cachedColumnSpinnerNodeIds.delete(nodeId);
  }

  syncRunningCachedColumnSpinners() {
    let runningNodes = this.getRunningCachedColumnNodes();
    let runningNodeIds = new Set(runningNodes.map(x => x.id));

    this.cachedColumnSpinnerNodeIds.forEach(nodeId => {
      let isNodeRunning = runningNodeIds.has(nodeId);

      if (isNodeRunning === false) {
        this.cachedColumnSpinnerNodeIds.delete(nodeId);
      }
    });

    runningNodes.forEach(node => {
      this.cachedColumnSpinnerNodeIds.add(node.id);
    });
  }

  getRunningCachedColumnNodes(): SchemaTreeNode[] {
    let columnNodes = this.getColumnNodes({ nodes: this.treeNodes });
    return columnNodes.filter(node => node.cachedColumn?.status === 'running');
  }

  startCachedColumnsPolling() {
    let hasPollingSub = isDefined(this.cachedColumnsPollingSub);

    if (hasPollingSub === true) {
      return;
    }

    let runningNodes = this.getRunningCachedColumnNodes();

    if (runningNodes.length === 0) {
      return;
    }

    this.cachedColumnsPollingSub = interval(2500)
      .pipe(exhaustMap(() => this.pollRunningCachedColumns()))
      .subscribe();
  }

  stopCachedColumnsPolling() {
    let hasPollingSub = isDefined(this.cachedColumnsPollingSub);

    if (hasPollingSub === false) {
      return;
    }

    this.cachedColumnsPollingSub.unsubscribe();
    this.cachedColumnsPollingSub = undefined;
  }

  pollRunningCachedColumns() {
    let runningNodes = this.getRunningCachedColumnNodes();

    if (runningNodes.length === 0) {
      this.stopCachedColumnsPolling();
      this.syncRunningCachedColumnSpinners();
      this.cd.detectChanges();
      return of(undefined);
    }

    let nav = this.navQuery.getValue();

    return this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendGetCachedColumns,
        payload: {
          projectId: nav.projectId,
          envId: nav.envId,
          columns: runningNodes.map(node => ({
            connectionId: node.connectionId,
            schemaName: node.schemaDisplayName,
            tableName: node.tableName,
            columnName: node.columnName
          }))
        }
      })
      .pipe(
        map((resp: ToBackendGetCachedColumnsResponse) => {
          if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            let returnedColumnIds = new Set(
              resp.payload.cachedColumns.map(
                cachedColumn =>
                  `${cachedColumn.connectionId}__${cachedColumn.schemaName}__${cachedColumn.tableName}__${cachedColumn.columnName}`
              )
            );

            resp.payload.cachedColumns.forEach(cachedColumn => {
              let node = runningNodes.find(
                x =>
                  x.connectionId === cachedColumn.connectionId &&
                  x.schemaDisplayName === cachedColumn.schemaName &&
                  x.tableName === cachedColumn.tableName &&
                  x.columnName === cachedColumn.columnName
              );

              let isNodeDefined = isDefined(node);

              if (isNodeDefined === true) {
                node.cachedColumn = cachedColumn;

                if (cachedColumn.status !== 'running') {
                  this.removeCachedColumnSpinner({ nodeId: node.id });
                }
              }
            });

            runningNodes.forEach(node => {
              let returnedColumnId = `${node.connectionId}__${node.schemaDisplayName}__${node.tableName}__${node.columnName}`;
              let isColumnReturned = returnedColumnIds.has(returnedColumnId);

              if (isColumnReturned === false) {
                node.cachedColumn = undefined;
                this.removeCachedColumnSpinner({ nodeId: node.id });
              }
            });

            this.syncCacheUniqueValuesRecommendationWarnings({
              nodes: this.treeNodes
            });
            this.applyFilter();
          }

          this.syncRunningCachedColumnSpinners();

          let stillRunningNodes = this.getRunningCachedColumnNodes();

          if (stillRunningNodes.length === 0) {
            this.stopCachedColumnsPolling();
          }

          this.cd.detectChanges();
        }),
        take(1)
      );
  }

  formatNumber(item: { value: number }): string {
    let { value } = item;
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  formatDate(item: { ts: number }): string {
    let { ts } = item;

    if (!isDefined(ts)) {
      return '';
    }

    return new Date(ts).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  formatDuration(item: { completedDurationMs: number }): string {
    let { completedDurationMs } = item;

    if (!isDefined(completedDurationMs)) {
      return '';
    }

    let secondsTotal = Math.round(completedDurationMs / 1000);
    let minutes = Math.floor(secondsTotal / 60);
    let seconds = secondsTotal % 60;

    if (minutes > 0) {
      return `${minutes} min ${seconds} seconds`;
    }

    return `${seconds} seconds`;
  }

  viewCachedColumn(item: { node: TreeNode; event: MouseEvent }) {
    let { node, event } = item;
    event.stopPropagation();

    let data = node.data as SchemaTreeNode;
    let nav = this.navQuery.getValue();

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendViewCachedColumn,
        payload: {
          projectId: nav.projectId,
          envId: nav.envId,
          connectionId: data.connectionId,
          schemaName: data.schemaDisplayName,
          tableName: data.tableName,
          columnName: data.columnName,
          offset: 0
        },
        showSpinner: true
      })
      .pipe(
        map((resp: ToBackendViewCachedColumnResponse) => {
          let dialogData: ViewCachedUniqueValuesDialogData = {
            title: `${data.connectionId} - ${data.schemaDisplayName}`,
            subtitle: data.tableName,
            columnName: data.columnName,
            cachedColumn: data.cachedColumn,
            columnNames: [],
            rows: [],
            errorMessage: undefined,
            projectId: nav.projectId,
            envId: nav.envId,
            connectionId: data.connectionId,
            schemaName: data.schemaDisplayName,
            tableName: data.tableName
          };

          let isRespPayloadErrorMessageDefined = isDefined(
            resp.payload.errorMessage
          );

          if (
            resp.info?.status === ResponseInfoStatusEnum.Ok &&
            isRespPayloadErrorMessageDefined
          ) {
            dialogData.errorMessage = resp.payload.errorMessage;
          } else if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            dialogData.cachedColumn = resp.payload.cachedColumn;
            dialogData.columnNames = resp.payload.columnNames;
            dialogData.rows = resp.payload.rows;
            this.updateCachedColumn({
              nodeId: data.id,
              cachedColumn: resp.payload.cachedColumn
            });
            this.applyFilter();
          } else {
            dialogData.errorMessage = 'Failed to fetch cached column';
          }

          this.myDialogService.showViewCachedUniqueValues(dialogData);
          this.cd.detectChanges();
        }),
        take(1)
      )
      .subscribe();
  }

  refreshCachedColumn(item: {
    node: TreeNode;
    event: MouseEvent;
    refreshType: 'full' | 'sample';
    sampleSize?: number;
  }) {
    let { node, event, refreshType, sampleSize } = item;
    event.stopPropagation();

    let data = node.data as SchemaTreeNode;
    let nav = this.navQuery.getValue();

    this.addCachedColumnSpinner({ nodeId: data.id });
    this.cd.detectChanges();

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendRefreshCachedColumn,
        payload: {
          projectId: nav.projectId,
          envId: nav.envId,
          connectionId: data.connectionId,
          schemaName: data.schemaDisplayName,
          tableName: data.tableName,
          columnName: data.columnName,
          refreshType: refreshType,
          sampleSize: sampleSize
        }
      })
      .pipe(
        map((resp: ToBackendRefreshCachedColumnResponse) => {
          if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            this.updateCachedColumn({
              nodeId: data.id,
              cachedColumn: resp.payload.cachedColumn
            });
            this.applyFilter();

            if (resp.payload.cachedColumn?.status === 'running') {
              this.startCachedColumnsPolling();
            } else {
              this.removeCachedColumnSpinner({ nodeId: data.id });
            }
          }
        }),
        finalize(() => {
          let columnNodes = this.getColumnNodes({ nodes: this.treeNodes });
          let columnNode = columnNodes.find(x => x.id === data.id);
          let isColumnStillRunning =
            columnNode?.cachedColumn?.status === 'running';

          if (isColumnStillRunning === false) {
            this.removeCachedColumnSpinner({ nodeId: data.id });
          }

          this.cd.detectChanges();
        }),
        take(1)
      )
      .subscribe();
  }

  refreshCachedColumnSample(item: { node: TreeNode; event: MouseEvent }) {
    let { node, event } = item;
    event.stopPropagation();

    this.myDialogService.showCacheColumnFromSample({
      sampleSize: 10000,
      onSubmit: sampleSize => {
        this.refreshCachedColumn({
          node: node,
          event: event,
          refreshType: 'sample',
          sampleSize: sampleSize
        });
      }
    });
  }

  clearCachedColumn(item: { node: TreeNode; event: MouseEvent }) {
    let { node, event } = item;
    event.stopPropagation();

    let data = node.data as SchemaTreeNode;
    let nav = this.navQuery.getValue();

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendClearCachedColumn,
        payload: {
          projectId: nav.projectId,
          envId: nav.envId,
          connectionId: data.connectionId,
          schemaName: data.schemaDisplayName,
          tableName: data.tableName,
          columnName: data.columnName
        },
        showSpinner: true
      })
      .pipe(
        map((resp: ToBackendClearCachedColumnResponse) => {
          if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            this.updateCachedColumn({
              nodeId: data.id,
              cachedColumn: undefined
            });
            this.removeCachedColumnSpinner({ nodeId: data.id });
            this.applyFilter();
            this.cd.detectChanges();
          }
        }),
        take(1)
      )
      .subscribe();
  }

  sampleOnClick(item: { node: TreeNode; event: MouseEvent }) {
    let { node, event } = item;
    event.stopPropagation();

    let data = node.data as SchemaTreeNode;

    let isColumn = data.nodeType === 'column';
    let title = `${data.connectionId} - ${data.schemaDisplayName}`;
    let subtitle = data.tableName;

    let nav = this.navQuery.getValue();

    this.sampleRequestId += 1;
    let currentSampleRequestId = this.sampleRequestId;

    this.sampleSpinnerNodeId = data.id;
    this.cd.detectChanges();

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendGetConnectionSample,
        payload: {
          projectId: nav.projectId,
          envId: nav.envId,
          connectionId: data.connectionId,
          schemaName: data.schemaDisplayName,
          tableName: data.tableName,
          columnName: isColumn ? data.columnName : undefined
        }
      })
      .pipe(
        map((resp: ToBackendGetConnectionSampleResponse) => {
          if (currentSampleRequestId !== this.sampleRequestId) {
            return;
          }

          let dialogData: SampleDialogData = {
            title: title,
            subtitle: subtitle,
            columnNames: [],
            rows: [],
            errorMessage: undefined,
            projectId: nav.projectId,
            envId: nav.envId,
            connectionId: data.connectionId,
            schemaName: data.schemaDisplayName,
            tableName: data.tableName,
            columnName: isColumn ? data.columnName : undefined
          };

          if (
            resp.info?.status === ResponseInfoStatusEnum.Ok &&
            isDefined(resp.payload.errorMessage)
          ) {
            dialogData.errorMessage = resp.payload.errorMessage;
          } else if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            dialogData.columnNames = resp.payload.columnNames;
            dialogData.rows = resp.payload.rows;
          } else {
            dialogData.errorMessage = 'Failed to fetch sample data';
          }

          this.myDialogService.showSample(dialogData);
        }),
        finalize(() => {
          if (currentSampleRequestId !== this.sampleRequestId) {
            return;
          }

          this.sampleSpinnerNodeId = undefined;
          this.cd.detectChanges();
        }),
        take(1)
      )
      .subscribe();
  }
}
